import { useCallback, useEffect } from 'react';
import useSandboxes, { SandboxIsoStateIds } from 'SandboxModule/hooks/useSandboxes';
import { SandboxStatusTypes } from 'SandboxModule/constants';
import { ValueOf } from 'type-fest';
import type { SearchableOption } from './SearchableSelect';

export const useRealmSearchableSelect = ({
  tenantName,
  isoId = SandboxIsoStateIds.AppbarRealmSelector,
}: {
  tenantName?: string;
  isoId?: ValueOf<typeof SandboxIsoStateIds>;
}) => {
  const { fetchSandboxes, getSandboxesByIsoState, getSandbox } = useSandboxes();

  useEffect(() => {
    fetchSandboxes(isoId);
  }, [fetchSandboxes, isoId]);

  const realmsIsoData = getSandboxesByIsoState(isoId);

  const getOptions = useCallback(
    (queryLC: string): SearchableOption[] => {
      return (realmsIsoData?.topLevelOrder || [])
        .filter((realmId: string) => {
          const sb = getSandbox(realmId);
          if (!sb) return false;
          if (tenantName && sb.tenant !== tenantName) return false;
          return sb.status !== SandboxStatusTypes.Archived && sb.is_ready;
        })
        .map((realmId: string) => {
          const sb = getSandbox(realmId)!;
          const label = `${sb.team_name} (${sb.realm_id})`;
          return { value: realmId, label };
        })
        .filter(({ label }) => (queryLC ? label.toLocaleLowerCase().includes(queryLC) : true))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    [realmsIsoData, getSandbox, tenantName],
  );

  return {
    getOptions,
    defaultLabel: 'Realm ID',
    defaultLabelId: 'realm-select-label',
    defaultPlaceholder: 'Search realms by name or ID',
  } as const;
};

export default useRealmSearchableSelect;
